import { useEffect, useState, forwardRef, type ReactElement, type Ref } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Zoom,
  alpha,
  useTheme,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Check from "@mui/icons-material/Check";
import Launch from "@mui/icons-material/Launch";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import confetti from "canvas-confetti";
import { showSuccessToast } from "../../../../components/common/ShowToast";

export interface VenuePublishedModalProps {
  open: boolean;
  onClose: () => void;
  venueName: string;
  venueType?: string;
  city?: string;
  publicUrl: string | null;
  coverPhotoUrl?: string | null;
  templateName?: string;
  templateAccent?: string;
}

const SmoothZoomTransition = forwardRef(function SmoothZoomTransition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return (
    <Zoom
      ref={ref}
      timeout={{ enter: 320, exit: 220 }}
      style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
      {...props}
    />
  );
});

export default function VenuePublishedModal({
  open,
  onClose,
  venueName,
  venueType,
  city,
  publicUrl,
  coverPhotoUrl,
  templateName,
  templateAccent,
}: VenuePublishedModalProps) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setCopied(false);
      try {
        confetti({
          particleCount: 70,
          spread: 65,
          origin: { y: 0.62 },
          colors: ["#7A5C48", "#C29363", "#10b981", "#f59e0b", "#3b82f6"],
          disableForReducedMotion: true,
        });
      } catch {
        // Safe fallback in test or headless environments
      }
    }
  }, [open]);

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      showSuccessToast("Listing link copied to clipboard");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      // Ignore if clipboard permissions are denied
    }
  };

  const displayUrl = publicUrl ? publicUrl.replace(/^https?:\/\//, "") : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SmoothZoomTransition}
      fullWidth
      maxWidth="xs"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(15, 23, 42, 0.55)",
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 4,
          p: { xs: 2.5, sm: 3 },
          position: "relative",
          overflow: "hidden",
          border: "1px solid",
          borderColor: (t) =>
            t.palette.mode === "dark" ? alpha("#fff", 0.1) : "rgba(0, 0, 0, 0.08)",
          boxShadow:
            "0 24px 50px -12px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          bgcolor: (t) =>
            t.palette.mode === "dark" ? "background.paper" : "#FFFFFF",
        },
      }}
    >
      {/* Top right close button */}
      <IconButton
        aria-label="Close published dialog"
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          top: 14,
          right: 14,
          zIndex: 5,
          color: "text.secondary",
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? alpha("#fff", 0.06)
              : alpha("#000", 0.04),
          "&:hover": {
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha("#fff", 0.12)
                : alpha("#000", 0.08),
            color: "text.primary",
          },
        }}
      >
        <Close sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Hero Icon & Live Badge */}
      <Stack alignItems="center" sx={{ textAlign: "center", pt: 1 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            bgcolor: alpha("#10b981", theme.palette.mode === "dark" ? 0.2 : 0.12),
            border: "1.5px solid",
            borderColor: alpha("#10b981", theme.palette.mode === "dark" ? 0.4 : 0.28),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 24px -2px rgba(16, 185, 129, 0.35)",
            animation: "pulseIcon 3s infinite ease-in-out",
            "@keyframes pulseIcon": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.05)" },
            },
          }}
        >
          <CheckCircleRounded sx={{ fontSize: 34, color: "#10b981" }} />
        </Box>

        {/* Live Pill */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.9,
            px: 1.4,
            py: 0.4,
            borderRadius: 99,
            bgcolor: alpha("#10b981", theme.palette.mode === "dark" ? 0.16 : 0.09),
            border: "1px solid",
            borderColor: alpha("#10b981", 0.25),
            mt: 1.75,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#10b981",
              boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.3)",
              animation: "liveDotPulse 2s infinite ease-in-out",
              "@keyframes liveDotPulse": {
                "0%": { transform: "scale(0.9)", opacity: 0.8 },
                "50%": { transform: "scale(1.2)", opacity: 1 },
                "100%": { transform: "scale(0.9)", opacity: 0.8 },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#059669",
            }}
          >
            Live on Muzukuru
          </Typography>
        </Box>

        {/* Headings */}
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 20, sm: 22 },
            letterSpacing: "-0.02em",
            mt: 1.5,
            color: "text.primary",
          }}
        >
          Venue is published!
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: 13.5,
            lineHeight: 1.5,
            mt: 0.75,
            px: { xs: 0, sm: 1 },
          }}
        >
          <strong>{venueName}</strong> is now live on the website. Visitors can explore photos, check layouts, and submit booking inquiries.
        </Typography>
      </Stack>

      {/* Venue Snippet Card Preview */}
      <Box
        sx={{
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          mt: 2.25,
          aspectRatio: "16 / 9",
          border: "1px solid",
          borderColor: (t) =>
            t.palette.mode === "dark" ? alpha("#fff", 0.1) : "rgba(0, 0, 0, 0.08)",
          boxShadow: "0 4px 18px -4px rgba(0, 0, 0, 0.1)",
          bgcolor: (t) => (t.palette.mode === "dark" ? "#1e293b" : "#f1f5f9"),
        }}
      >
        {coverPhotoUrl ? (
          <Box
            component="img"
            src={coverPhotoUrl}
            alt={venueName}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: alpha("#fff", 0.4),
            }}
          >
            <PublicOutlined sx={{ fontSize: 48 }} />
          </Box>
        )}

        {/* Gradient Overlay and Card Content */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0.3) 55%, transparent 100%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 1.75,
          }}
        >
          {/* Top pill (Template name) */}
          <Stack direction="row" justifyContent="flex-end">
            {templateName && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.15,
                  py: 0.35,
                  borderRadius: 2,
                  bgcolor: "rgba(0, 0, 0, 0.65)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: templateAccent || "#0ea5e9",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    letterSpacing: 0.4,
                  }}
                >
                  {templateName}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Bottom venue info */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              noWrap
              sx={{ color: "#FFFFFF", lineHeight: 1.25, fontSize: 15 }}
            >
              {venueName}
            </Typography>
            {(city || venueType) && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: "rgba(255, 255, 255, 0.82)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.25,
                  fontSize: 11.5,
                }}
              >
                <LocationOnOutlined sx={{ fontSize: 13 }} />
                {[venueType, city].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Copyable URL Bar */}
      {publicUrl && (
        <Box
          onClick={handleCopy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              void handleCopy();
            }
          }}
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            p: 1.1,
            pl: 1.5,
            borderRadius: 2.5,
            bgcolor: (t) =>
              t.palette.mode === "dark" ? alpha("#fff", 0.05) : "#F8FAFC",
            border: "1px solid",
            borderColor: (t) =>
              t.palette.mode === "dark" ? alpha("#fff", 0.1) : "#E2E8F0",
            cursor: "pointer",
            transition: "all 0.18s ease",
            "&:hover": {
              borderColor: (t) =>
                t.palette.mode === "dark" ? alpha("#fff", 0.25) : "#CBD5E1",
              bgcolor: (t) =>
                t.palette.mode === "dark" ? alpha("#fff", 0.08) : "#F1F5F9",
            },
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <PublicOutlined
              sx={{ fontSize: 17, color: "text.secondary", flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 12,
                color: "text.primary",
                fontWeight: 500,
              }}
            >
              {displayUrl}
            </Typography>
          </Stack>

          <Tooltip title={copied ? "Copied!" : "Copy link"} arrow>
            <Button
              size="small"
              startIcon={
                copied ? (
                  <Check sx={{ fontSize: 14 }} />
                ) : (
                  <ContentCopy sx={{ fontSize: 14 }} />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                void handleCopy();
              }}
              sx={{
                textTransform: "none",
                fontSize: 11.5,
                fontWeight: 700,
                px: 1.2,
                py: 0.35,
                minWidth: 72,
                borderRadius: 2,
                bgcolor: copied
                  ? alpha("#10b981", 0.12)
                  : (t) =>
                      t.palette.mode === "dark" ? alpha("#fff", 0.08) : "#FFFFFF",
                color: copied ? "#059669" : "text.primary",
                border: "1px solid",
                borderColor: copied
                  ? alpha("#10b981", 0.3)
                  : (t) =>
                      t.palette.mode === "dark"
                        ? alpha("#fff", 0.15)
                        : "#E2E8F0",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                "&:hover": {
                  bgcolor: copied
                    ? alpha("#10b981", 0.18)
                    : (t) =>
                        t.palette.mode === "dark"
                          ? alpha("#fff", 0.14)
                          : "#F8FAFC",
                },
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </Tooltip>
        </Box>
      )}

      {/* Action Buttons */}
      <Stack spacing={1} sx={{ mt: 2.5 }}>
        {publicUrl && (
          <Button
            variant="contained"
            fullWidth
            component="a"
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<Launch sx={{ fontSize: 17 }} />}
            sx={{
              py: 1.25,
              borderRadius: 2.5,
              textTransform: "none",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 0.2,
              bgcolor: "#7A5C48",
              color: "#FFFFFF",
              boxShadow: "0 4px 14px 0 rgba(122, 92, 72, 0.3)",
              transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
              "&:hover": {
                bgcolor: "#684b39",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 20px 0 rgba(122, 92, 72, 0.42)",
              },
            }}
          >
            View venue on website
          </Button>
        )}

        <Button
          fullWidth
          onClick={onClose}
          sx={{
            py: 0.85,
            borderRadius: 2.5,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            color: "text.secondary",
            "&:hover": {
              bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
              color: "text.primary",
            },
          }}
        >
          Done
        </Button>
      </Stack>
    </Dialog>
  );
}
